import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { Dayjs } from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

import {
  GROUP_CHANGE_SUPPRESSION_MS,
  LOAD_MORE_ROWS_THRESHOLD_PX,
  PLANNER_PAGINATION_LOG_TAG,
} from '@/modules/planner';
import { logger } from '@/utils/logger/logger';

import usePlannerStore from '../../store';
import { usePlannerViewportState } from '../planner-base/use-planner-viewport-state';
import { usePlannerHorizontalLifecycle } from './use-planner-horizontal-lifecycle';
import { usePlannerScrollEffects } from './use-planner-scroll-effects';

interface UsePlannerScrollManagerParams {
  rowsLength: number;
  visibleRowsLength: number;
  cellWidthPx: number;
  isLoading: boolean;
  /** Placeholder-data refresh (keepPreviousData) — a filter reload flips this, not isLoading */
  isRefreshing?: boolean;
  onLoadDatesLeft?: () => Promise<void>;
  onLoadDatesRight?: () => Promise<void>;
  onLoadMoreRows: () => void;
  hasMoreRows: boolean;
  isLoadingMoreRows: boolean;
}

interface UsePlannerScrollManagerResult {
  dates: Dayjs[];
  parentRef: RefObject<HTMLDivElement | null>;
  shouldShowSkeletonRows: boolean;
}

export const usePlannerScrollManager = ({
  rowsLength,
  visibleRowsLength,
  cellWidthPx,
  isLoading,
  isRefreshing = false,
  onLoadDatesLeft,
  onLoadDatesRight,
  onLoadMoreRows,
  hasMoreRows,
  isLoadingMoreRows,
}: UsePlannerScrollManagerParams): UsePlannerScrollManagerResult => {
  const {
    dates,
    groupBy,
    loadDatesLeft,
    loadDatesRight,
    expandDatesLeft,
    expandDatesRight,
    normalizeDatesWindow,
    setFirstVisibleDate,
    setVisibleDateRange,
    setIsScrolling,
    pendingScrollTarget,
    setPendingScrollTarget,
    isDragResizeInteracting,
    isLoadingDateRange,
    collapsedGroups,
  } = usePlannerStore(
    useShallow(store => ({
      dates: store.dates,
      groupBy: store.groupBy,
      loadDatesLeft: store.loadDatesLeft,
      loadDatesRight: store.loadDatesRight,
      expandDatesLeft: store.expandDatesLeft,
      expandDatesRight: store.expandDatesRight,
      normalizeDatesWindow: store.normalizeDatesWindow,
      setFirstVisibleDate: store.setFirstVisibleDate,
      setVisibleDateRange: store.setVisibleDateRange,
      setIsScrolling: store.setIsScrolling,
      pendingScrollTarget: store.pendingScrollTarget,
      setPendingScrollTarget: store.setPendingScrollTarget,
      isDragResizeInteracting: store.isDragResizeInteracting,
      isLoadingDateRange: store.isLoadingDateRange,
      collapsedGroups: store.collapsedGroups,
    }))
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const lastScrollLeftRef = useRef(0);
  const isExtendingLeftRef = useRef(false);
  const isExtendingRightRef = useRef(false);
  const isLoadingLeftAsyncRef = useRef(false);
  const isLoadingRightAsyncRef = useRef(false);
  const pendingLeftCompensationRef = useRef<number | null>(null);
  const pendingRightCompensationRef = useRef<number | null>(null);
  const nextLeftLoadTriggerScrollLeftRef = useRef<number | null>(null);
  const nextRightLoadTriggerScrollLeftRef = useRef<number | null>(null);
  const pendingLeftAsyncFetchRef = useRef(false);
  const pendingRightAsyncFetchRef = useRef(false);
  const paginationSettleRafRef = useRef<number | null>(null);
  const lastWheelAtRef = useRef(-Infinity);
  const programmaticHorizontalScrollUntilRef = useRef(-Infinity);
  const isMouseButtonDownRef = useRef(false);
  const lastNonWheelHorizontalScrollAtRef = useRef(-Infinity);
  const pendingNormalizedScrollLeftRef = useRef<number | null>(null);
  const pendingExpandedLeftInteractionScrollLeftRef = useRef<number | null>(null);
  const expandedWindowDuringInteractionRef = useRef(false);
  const nextNonWheelLeftExpandAllowedAtRef = useRef(-Infinity);
  const nextNonWheelRightExpandAllowedAtRef = useRef(-Infinity);
  const isNearBottomRef = useRef(false);
  const onLoadMoreRowsRef = useRef(onLoadMoreRows);
  const hasMoreRowsRef = useRef(hasMoreRows);
  const isLoadingMoreRowsRef = useRef(isLoadingMoreRows);
  const verticalPaginationSuppressedRef = useRef(false);
  const prevGroupByRef = useRef(groupBy);
  const scrollTimeoutRef = useRef<number | null>(null);

  const shouldShowSkeletonRows =
    isLoadingMoreRows && hasMoreRowsRef.current && !isLoadingDateRange && isNearBottomRef.current;

  useEffect(() => {
    if (prevGroupByRef.current !== groupBy) {
      prevGroupByRef.current = groupBy;
      verticalPaginationSuppressedRef.current = true;

      const timer = setTimeout(() => {
        verticalPaginationSuppressedRef.current = false;
      }, GROUP_CHANGE_SUPPRESSION_MS);

      return () => clearTimeout(timer);
    }
  }, [groupBy]);

  const startAsyncLeftFetch = useCallback(
    (reason: string) => {
      if (!onLoadDatesLeft || isLoadingLeftAsyncRef.current) {
        return;
      }

      isLoadingLeftAsyncRef.current = true;
      onLoadDatesLeft().finally(() => {
        isLoadingLeftAsyncRef.current = false;
        logger.debug(PLANNER_PAGINATION_LOG_TAG, `${reason}: LEFT async fetch completed`);
      });
    },
    [onLoadDatesLeft]
  );

  const startAsyncRightFetch = useCallback(
    (reason: string) => {
      if (!onLoadDatesRight || isLoadingRightAsyncRef.current) {
        return;
      }

      isLoadingRightAsyncRef.current = true;
      onLoadDatesRight().finally(() => {
        isLoadingRightAsyncRef.current = false;
        logger.debug(PLANNER_PAGINATION_LOG_TAG, `${reason}: RIGHT async fetch completed`);
      });
    },
    [onLoadDatesRight]
  );

  const { lastHorizontalScrollLeftRef, updateVisibleDateRange, updateFirstVisibleDate } =
    usePlannerViewportState({
      parentRef,
      cellWidthPx,
      dates,
      isLoading,
      isDragResizeInteracting,
      visibleRowsLength,
      setVisibleDateRange,
      setFirstVisibleDate,
      isDragResizeInteractionActive: () => usePlannerStore.getState().isDragResizeInteracting,
    });

  const sharedRefs = useMemo(
    () => ({
      lastScrollLeftRef,
      isExtendingLeftRef,
      isExtendingRightRef,
      isLoadingLeftAsyncRef,
      isLoadingRightAsyncRef,
      pendingLeftCompensationRef,
      pendingRightCompensationRef,
      nextLeftLoadTriggerScrollLeftRef,
      nextRightLoadTriggerScrollLeftRef,
      pendingLeftAsyncFetchRef,
      pendingRightAsyncFetchRef,
      paginationSettleRafRef,
      programmaticHorizontalScrollUntilRef,
      isMouseButtonDownRef,
      pendingNormalizedScrollLeftRef,
      pendingExpandedLeftInteractionScrollLeftRef,
      expandedWindowDuringInteractionRef,
    }),
    []
  );

  const scrollEffectRefs = useMemo(
    () => ({
      ...sharedRefs,
      lastWheelAtRef,
      lastNonWheelHorizontalScrollAtRef,
      nextNonWheelLeftExpandAllowedAtRef,
      nextNonWheelRightExpandAllowedAtRef,
      lastHorizontalScrollLeftRef,
      isNearBottomRef,
      onLoadMoreRowsRef,
      hasMoreRowsRef,
      isLoadingMoreRowsRef,
      verticalPaginationSuppressedRef,
      scrollTimeoutRef,
    }),
    [lastHorizontalScrollLeftRef, sharedRefs]
  );

  const { normalizeExpandedDatesWindow } = usePlannerHorizontalLifecycle({
    parentRef,
    rowsLength,
    cellWidthPx,
    dates,
    isLoading,
    isRefreshing,
    onLoadDatesLeft,
    onLoadDatesRight,
    normalizeDatesWindow,
    pendingScrollTarget,
    setPendingScrollTarget,
    updateVisibleDateRange,
    updateFirstVisibleDate,
    startAsyncLeftFetch,
    startAsyncRightFetch,
    refs: sharedRefs,
  });

  usePlannerScrollEffects({
    parentRef,
    cellWidthPx,
    dates,
    visibleRowsLength,
    onLoadDatesLeft,
    onLoadDatesRight,
    loadDatesLeft,
    loadDatesRight,
    expandDatesLeft,
    expandDatesRight,
    setIsScrolling,
    normalizeExpandedDatesWindow,
    updateVisibleDateRange,
    updateFirstVisibleDate,
    startAsyncLeftFetch,
    startAsyncRightFetch,
    refs: scrollEffectRefs,
  });

  useEffect(() => {
    onLoadMoreRowsRef.current = onLoadMoreRows;
  }, [onLoadMoreRows]);

  useEffect(() => {
    hasMoreRowsRef.current = hasMoreRows;
  }, [hasMoreRows]);

  useEffect(() => {
    isLoadingMoreRowsRef.current = isLoadingMoreRows;
  }, [isLoadingMoreRows]);

  // Auto-fill: vertical pagination is otherwise only triggered by the scroll handler.
  // When rows shrink without a scroll event — collapsing a department/project group, or a
  // page whose groups hold more members than fit the viewport — the bottom can come into
  // view (or the content becomes shorter than the viewport entirely) and no scroll fires,
  // so the next page is never requested and pagination stalls. This effect re-checks the
  // near-bottom condition whenever the rendered rows / collapse state / pagination flags
  // change and loads more until the viewport is filled (scrollable) or no rows remain.
  // Geometry is read after a double rAF so the virtualizer has committed the new height.
  useLayoutEffect(() => {
    const container = parentRef.current;
    if (!container) return;
    if (!hasMoreRows || isLoadingMoreRows) return;
    if (verticalPaginationSuppressedRef.current || isLoadingDateRange) return;

    let rafId = 0;
    let innerRafId = 0;

    rafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        const el = parentRef.current;
        if (!el) return;
        if (!hasMoreRowsRef.current || isLoadingMoreRowsRef.current) return;
        if (verticalPaginationSuppressedRef.current) return;

        const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        // Covers both "content shorter than viewport" (negative/zero distance) and
        // "scrolled near the bottom after a collapse" within the normal threshold.
        if (distanceToBottom < LOAD_MORE_ROWS_THRESHOLD_PX) {
          logger.debug(
            PLANNER_PAGINATION_LOG_TAG,
            'auto-fill: requesting next page distToBottom=' +
              Math.round(distanceToBottom) +
              ' visibleRows=' +
              visibleRowsLength
          );
          isLoadingMoreRowsRef.current = true;
          onLoadMoreRowsRef.current();
        }
      });
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (innerRafId) cancelAnimationFrame(innerRafId);
    };
  }, [
    parentRef,
    visibleRowsLength,
    collapsedGroups,
    hasMoreRows,
    isLoadingMoreRows,
    isLoadingDateRange,
  ]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
        setIsScrolling(false);
      }

      if (paginationSettleRafRef.current !== null) {
        cancelAnimationFrame(paginationSettleRafRef.current);
      }
    };
  }, [setIsScrolling]);

  return {
    dates,
    parentRef,
    shouldShowSkeletonRows,
  };
};
